import './DivergenceMethodology.css';

const AXES = [
  {
    name: 'Referent',
    tier: 4,
    color: 'var(--paal-inbam)',
    definition:
      'The commentators disagree about what the verse is fundamentally about — a different whole-verse subject or theological target.',
    example:
      'One reads the kural as praising the Jain Arhat; the other as praising Viṣṇu.',
  },
  {
    name: 'Frame',
    tier: 3,
    color: 'var(--paal-porul)',
    definition:
      'One commentator fits the verse into a named scheme the other’s plain reading rejects.',
    example: (
      <>
        The <span lang="ta">சாங்கிய</span> (Sāṅkhya) 25-tattva scheme, the eight
        yogic siddhis, the Jain <span lang="ta">தருமத்தியானம்</span> (dharma-dhyāna).
      </>
    ),
  },
  {
    name: 'Gloss',
    tier: 2,
    color: 'var(--paal-aram)',
    definition:
      'A single word is glossed one way and another, and the two glosses cannot both be true.',
    example: (
      <>
        <span lang="ta">ஒழுக்கு</span> read as “moral conduct” vs. “the flowing
        of water.”
      </>
    ),
  },
  {
    name: 'Emphasis',
    tier: 1,
    color: 'var(--text-muted)',
    definition:
      'Same subject, but the two pull toward opposed poles or significances.',
    example:
      'One stresses the harm of the vice; the other stresses the merit of the opposing virtue.',
  },
];

const TIERS = [
  { n: 4, name: 'Referent',   short: 'Different subject entirely.',                              color: 'var(--paal-inbam)' },
  { n: 3, name: 'Framework',  short: 'Imported scheme that the plain reading rejects.',           color: 'var(--paal-porul)' },
  { n: 2, name: 'Gloss',      short: 'Two glosses of a key word that cannot both be true.',       color: 'var(--paal-aram)' },
  { n: 1, name: 'Emphasis',   short: 'Same subject, opposed emphasis.',                           color: 'var(--text-muted)' },
  { n: 0, name: 'Agreement',  short: 'Read the same way; only elaboration differs.',              color: 'var(--rule-strong)' },
];

export default function DivergenceMethodology() {
  return (
    <section
      id="method"
      className="dvmethod"
      aria-labelledby="dvmethod-heading"
    >
      <header className="dvmethod__head">
        <p className="dvmethod__eyebrow">Methodology</p>
        <h2 id="dvmethod-heading" className="dvmethod__title">
          How this map was made
        </h2>
        <p className="dvmethod__lede">
          For every one of the 1,330 kurals, a frontier LLM reads both{' '}
          Manakkudavar's (<span lang="ta">மணக்குடவர்</span>, ~10th c.) and
          Parimelazhagar's (<span lang="ta">பரிமேலழகர்</span>, ~13th c.) urai
          and answers four yes/no questions about whether they disagree. A
          verse's tier is the deepest disagreement present, not a sum — so a
          verse with two overlapping disagreements lands at the tier of the
          more severe one, not higher.
        </p>
      </header>

      <div className="dvmethod__axes" aria-labelledby="dvmethod-axes-h">
        <h3 id="dvmethod-axes-h" className="dvmethod__section-title">
          The four axes of disagreement
        </h3>
        <ul className="dvmethod__axes-grid">
          {AXES.map((a) => (
            <li
              key={a.name}
              className="dvmethod__axis-card"
              style={{ '--dvmethod-axis-color': a.color } as React.CSSProperties}
            >
              <div className="dvmethod__axis-head">
                <h4 className="dvmethod__axis-name">{a.name}</h4>
                <span className="dvmethod__axis-tier">Tier {a.tier}</span>
              </div>
              <p className="dvmethod__axis-def">{a.definition}</p>
              <p className="dvmethod__axis-eg">
                <em>Example.</em> {a.example}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="dvmethod__tiers" aria-labelledby="dvmethod-tiers-h">
        <h3 id="dvmethod-tiers-h" className="dvmethod__section-title">
          The tier scale
        </h3>
        <ol className="dvmethod__tier-list" aria-label="Tiers, highest to lowest">
          {TIERS.map((t) => (
            <li
              key={t.n}
              className="dvmethod__tier-row"
              style={{ '--dvmethod-tier-color': t.color } as React.CSSProperties}
            >
              <span className="dvmethod__tier-badge">Tier {t.n}</span>
              <span className="dvmethod__tier-name">{t.name}</span>
              <span className="dvmethod__tier-short">{t.short}</span>
            </li>
          ))}
        </ol>
        <p className="dvmethod__note">
          <strong>Precedence.</strong> A single verse can fire more than one
          axis. The tier reflects the deepest one present, in the order
          Referent&nbsp;&gt;&nbsp;Frame&nbsp;&gt;&nbsp;Gloss&nbsp;&gt;&nbsp;Emphasis.
          The score is computed in Python from the four booleans — the model
          never sees the tier rule.
        </p>
      </div>

      <details className="dvmethod__deep">
        <summary className="dvmethod__deep-summary">
          <span>The instrument</span>
          <span className="dvmethod__deep-hint">Prompt, tool schema, guardrails</span>
        </summary>
        <div className="dvmethod__deep-body">
          <p>
            The reader is Anthropic's <strong>Claude Opus 4.8</strong> on
            Amazon Bedrock, called with a calibrated system prompt (identical
            across all 1,330 verses) and a forced tool call that returns a
            structured record with four boolean axes, a crux term, one Tamil
            quote from each commentator, and a one-line English reason.
            Forced tool use makes the output shape unambiguous; the model
            can't emit prose we then have to parse.
          </p>
          <p>Two guardrails written into the prompt keep the axes honest:</p>
          <ul className="dvmethod__list">
            <li>
              <strong>Silence is not disagreement.</strong> If one commentator
              elaborates on something the other omits, that's added detail,
              not divergence. No axis fires.
            </li>
            <li>
              <strong>Containment is not disagreement.</strong> If one gloss
              is a broader case that includes the other (e.g. “this world and
              the higher world” contains “the world of liberation”), they are
              compatible; gloss stays FALSE. Only mutual exclusion counts.
            </li>
          </ul>
          <p>
            The tier is <em>never</em> emitted by the model. Python collapses
            the four booleans to a 0–4 tier using the precedence rule, so the
            rule is deterministic and lives in one place.
          </p>
        </div>
      </details>

      <details className="dvmethod__deep">
        <summary className="dvmethod__deep-summary">
          <span>Reliability discipline</span>
          <span className="dvmethod__deep-hint">Anchors, runs, consensus, source-check</span>
        </summary>
        <div className="dvmethod__deep-body">
          <p>
            Scoring 1,330 verses with a language model without safeguards
            would be an unreliable classifier at scale. Four checks keep this
            one honest:
          </p>
          <ul className="dvmethod__list">
            <li>
              <strong>Calibrated prompt.</strong> Verbatim across all runs.
              Any prompt change resets calibration.
            </li>
            <li>
              <strong>Anchor set.</strong> Ten verses with human-checked known
              tiers cover no-divergence, gloss, framework and boundary cases.
              We score just those ten, three runs each, and confirm the
              majority tier matches the expected board <em>before</em>
              spending on the full corpus. A miss stops the pipeline.
            </li>
            <li>
              <strong>Three runs per verse.</strong>
              <br />
              <span className="dvmethod__sub">Unanimous (3 / 3)</span> → high
              confidence.{' '}
              <span className="dvmethod__sub">Majority (2 / 3)</span> → medium
              confidence.{' '}
              <span className="dvmethod__sub">Split (1 / 1 / 1)</span> → low
              confidence, flagged for human review.
            </li>
            <li>
              <strong>Source-integrity sweep.</strong> Before scoring, we scan
              the Manakkudavar text for duplicate urai across verses and for
              urai that don't share content words with their own verse.
              Twelve verses turned out to have wrong-slot urai in the public
              scrape we started from &mdash; those were corrected against a
              print critical edition and re-scored before the tiers on this
              page were computed.
            </li>
          </ul>
        </div>
      </details>

      <details className="dvmethod__deep">
        <summary className="dvmethod__deep-summary">
          <span>What this is not</span>
          <span className="dvmethod__deep-hint">Limits and caveats</span>
        </summary>
        <div className="dvmethod__deep-body">
          <ul className="dvmethod__list">
            <li>
              <strong>Not a claim that either commentator is right.</strong>{' '}
              The tier measures the depth of disagreement, not who to believe.
            </li>
            <li>
              <strong>Not a substitute for scholarly reading.</strong> Where
              tier is high and agreement is unanimous, the disagreement is
              very likely real and worth an expert's attention. Where tier is
              low, or agreement is split, treat the row as a lead, not a
              verdict.
            </li>
            <li>
              <strong>Not a translation.</strong> Every Tamil quote in the
              reading room is the commentator's own words, not the model's
              paraphrase. The English “why this matters” line is the model's
              summary of the difference, provided for readers who cannot
              parse the medieval Tamil directly.
            </li>
            <li>
              <strong>The four axes are a decomposition, not a taxonomy.</strong>{' '}
              A verse can fire more than one. Whether emphasis is
              distinguishable from gloss on every edge case, and whether the
              precedence rule always matches human intuition, are open
              questions.
            </li>
          </ul>
        </div>
      </details>

      <footer className="dvmethod__foot">
        <p>
          The Manakkudavar and Parimelazhagar urai are in the public domain.
          Every reading here can be reproduced by re-running the calibrated
          prompt against the same text.
        </p>
      </footer>
    </section>
  );
}
